import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import t from './locales';
import { ICoupon } from './types';

interface IProps {
  coupons: ICoupon[];
  isPartnerEc: boolean;
  onSelectCoupon: (coupon: ICoupon) => void;
}

const Container = styled.div`
  width: 100%;
  cursor: pointer;
`;

const InputGroup = styled.div`
  display: flex;
  width: 100%;
  margin-top: 10px;
`;

const CouponInput = styled.input`
  flex: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: 0;
  box-shadow: none;
  height: 38px;
`;

const RegisterButton = styled.button<{ disabled: boolean }>`
  background-color: #007bff;
  color: white;
  border: 1px solid #007bff;
  border-radius: 0 5px 5px 0;
  padding: 0 20px;
  display: flex;
  align-items: center;
  height: 38px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${({ disabled }) => (disabled ? '#007bff' : '#0056b3')};
  }
`;

const CouponCard = styled.div<{ selected: boolean, newlyAdded?: boolean }>`
  border: 1px solid ${props => (props.selected ? '#007bff' : '#ddd')};
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  background-color: ${props => (props.selected ? '#e6f7ff' : 'white')};
  display: flex;
  align-items: center;
  width: 100%;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  @keyframes glow {
    0% {
      box-shadow: 0 0 0px #4FA3FA;
    }
    50% {
      box-shadow: 0 0 10px #4FA3FA;
    }
    100% {
      box-shadow: 0 0 0px #4FA3FA;
    }
  }

  ${props => props.newlyAdded && 'animation: glow 1.5s ease-in-out;'}
`;

const CouponSelector = styled.input`
  margin-right: 10px;
  transform: scale(1.5);
  accent-color: #007bff;
`;

const CouponDetails = styled.div`
  margin-left: 10px;
`;

const CouponInterface: React.FC<IProps> = ({ coupons: initialCoupons, onSelectCoupon }) => {
  const [coupons, setCoupons] = useState<ICoupon[]>(initialCoupons);
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);
  const [couponCode, setCouponCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [newlyAddedCouponId, setNewlyAddedCouponId] = useState<number | null>(null);

  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const handleCouponSelect = (id: number) => {
    setSelectedCouponId(id);

    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = id.toString();
    }

    const selectedCoupon = coupons.find(coupon => coupon.id === id);
    if (selectedCoupon) {
      onSelectCoupon(selectedCoupon);
    }
  };

  const handleCouponCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCouponCode(event.target.value);
  };

  const handleRegisterCoupon = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/snow/api/v1/coupons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ coupon_code: couponCode }),
      });

      const data: { user_coupon: ICoupon, error?: string } = await response.json();

      if (!response.ok) {
        alert(data.error);
        throw new Error(data.error);
      }

      const newCoupon: ICoupon = data.user_coupon;
      setCoupons((prevCoupons) => [...prevCoupons, newCoupon]);
      setNewlyAddedCouponId(newCoupon.id);
    } catch (error) {
      console.error('Error registering coupon:', error);
    } finally {
      setLoading(false);
    }

    setCouponCode('');
  };

  return (
    <div>
      <p className="font-weight-bold">{t.useCoupon()}</p>
      <Container>
        {coupons.length > 0 && coupons.map(coupon => (
          <CouponCard
            key={coupon.id}
            selected={coupon.id === selectedCouponId}
            newlyAdded={coupon.id === newlyAddedCouponId}
            onClick={() => handleCouponSelect(coupon.id)}
          >
            <CouponSelector
              type="radio"
              name="coupon"
              checked={coupon.id === selectedCouponId}
              onChange={() => handleCouponSelect(coupon.id)}
            />
            <CouponDetails>
              <p className="m-0 font-weight-bold">{coupon.name}</p>
              <p className="m-0 text-muted">{coupon.expireAt}</p>
            </CouponDetails>
          </CouponCard>
        ))}

        <form onSubmit={handleRegisterCoupon} className="form-group mt-3">
          <InputGroup>
            <CouponInput
              type="text"
              name="couponCode"
              className="form-control"
              placeholder={t.enterCouponCode()}
              value={couponCode}
              onChange={handleCouponCodeChange}
            />
            <RegisterButton type="submit" disabled={!couponCode || loading}>
              {loading ? <i className="fas fa-spinner fa-spin"></i> : t.add()}
            </RegisterButton>
          </InputGroup>
        </form>
      </Container>

      <input
        type="hidden"
        id="selectedCouponId"
        name="user_coupon_id"
        ref={hiddenInputRef}
        value={selectedCouponId?.toString() || ''}
      />
    </div>
  );
};

export default CouponInterface;
